import { useEffect } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { KitchenHoldAllChip } from "./KitchenHoldAll";
import { OrderPanelContent } from "./OrderPanelContent";
import type { KitchenHoldControls } from "./useKitchenHoldChoice";

interface OrderItem {
  itemId: number;
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>; // addonId -> { itemId -> количество }
  comment?: string;
}

interface OrderSheetProps {
  open: boolean;
  onClose: () => void;
  orderItems: OrderItem[];
  comment: string;
  showCommentInput: boolean;
  onCommentChange: (comment: string) => void;
  onToggleComment: () => void;
  onQuantityChange: (index: number, delta: number) => void;
  /** Отваря модала за редакция на модификатори/коментар от молива на реда (виж OrderPanelContent). */
  onItemEdit: (indices: number[], orderItem: OrderItem) => void;
  onSubmit: () => void;
  submitting: boolean;
  /** Клиент с отстъпка/надбавка по сметката — виж OrderPanelContent. */
  billDiscount?: { percent: number; clientName: string | null } | null;
  /** Времето за кухнята — виж OrderPanelContent; хапчето е в заглавието. */
  kitchenHold?: KitchenHoldControls;
}

/**
 * Мобилен bottom sheet „Преглед на поръчката" — отваря се от MobileOrderBar.
 * Тялото е споделеното OrderPanelContent (variant="sheet"); затварянето НЕ е
 * отказ от поръчката — кошницата се пази.
 */
export const OrderSheet = ({
  open,
  onClose,
  orderItems,
  comment,
  showCommentInput,
  onCommentChange,
  onToggleComment,
  onQuantityChange,
  onItemEdit,
  onSubmit,
  submitting,
  billDiscount = null,
  kitchenHold,
}: OrderSheetProps) => {
  const { t } = useTranslation();

  // Escape затваря sheet-а, докато е отворен (без да стига до други handlers)
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Скрим — клик извън панела затваря sheet-а */}
      <div
        className="fixed inset-0 bg-scrim z-[60]"
        onClick={onClose}
      />

      {/* Панел на sheet-а */}
      <div className="dk-elevated fixed inset-x-0 bottom-0 z-[70] bg-pos-card rounded-t-[22px] max-h-[78%] flex flex-col shadow-[0_-14px_44px_rgba(0,0,0,.25)] pb-[calc(10px+env(safe-area-inset-bottom,0px)*0.5)]">
        {/* Хедър: заглавие + брояч + времето за кухнята + бутон за затваряне.
            justify-between (не ml-auto на бутона) — antd глобалният reset
            слага button{margin:0} извън @layer и бие Tailwind ml-auto. */}
        <div className="px-4 pt-4 pb-3 flex items-center justify-between gap-2">
          <span className="min-w-0 flex-1 truncate text-base font-extrabold text-pos-ink">
            {t("ordering.orderSheetTitle")} ·{" "}
            {t("ordering.productsShortCount", { count: orderItems.length })}
          </span>
          {kitchenHold && <KitchenHoldAllChip controls={kitchenHold} />}
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 w-[34px] h-[34px] rounded-[10px] border border-pos-line2 grid place-items-center text-pos-dim cursor-pointer"
          >
            <CloseOutlined className="text-[14px]" />
          </button>
        </div>

        <OrderPanelContent
          orderItems={orderItems}
          comment={comment}
          showCommentInput={showCommentInput}
          onCommentChange={onCommentChange}
          onToggleComment={onToggleComment}
          onQuantityChange={onQuantityChange}
          onItemEdit={onItemEdit}
          onSubmit={onSubmit}
          submitting={submitting}
          variant="sheet"
          billDiscount={billDiscount}
          kitchenHold={kitchenHold}
        />
      </div>
    </>
  );
};
